<template>
  <!-- 单个模块的加载状态：页面上每个模块各自用一个，先到先显示，慢的那块单独提示，不再整页骨架。
       loading 时先显示骨架（#skeleton 插槽，没给就用一个简单的占位）；
       超过 slowAfter 还没回来，在骨架上加「加载中…」；超过 timeout 或 error 为真，显示提示和「重试」。
       重试只发 retry 事件，重新请求由父组件做（记得丢弃过期的旧响应）；重新发请求时把 attempt 加 1 让计时从头开始。 -->
  <div class="module-load-state">
    <template v-if="loading || error">
      <!-- key 不能去掉：没有 key 时，加载完切到默认插槽，Vue 会把这里的 div 原地复用成插槽里第一个 div
           （比如首页的 .platform-row、.other-panel），复用的元素带的是本组件的 scoped 标记而不是父组件的，
           父组件的 scoped 样式就全部失效（首页 Other platforms 变成通栏无底色的列表就是这么来的）。 -->
      <div key="mls-body" class="mls-body" :style="{ minHeight: minHeight }">
        <div class="mls-skeleton" :class="{ 'is-dimmed': phase !== 'loading' || error }">
          <slot name="skeleton">
            <div class="sk-card mls-default-skeleton">
              <div class="sk-bone" style="width:160px;height:16px;margin-bottom:16px"></div>
              <div class="sk-bone" style="width:100%;height:12px;margin-bottom:10px"></div>
              <div class="sk-bone" style="width:90%;height:12px;margin-bottom:10px"></div>
              <div class="sk-bone" style="width:70%;height:12px"></div>
            </div>
          </slot>
        </div>
        <div v-if="error || phase !== 'loading'" class="mls-overlay" role="status" aria-live="polite">
          <template v-if="error || phase === 'timeout'">
            <p class="mls-text">{{ error ? $t('ModuleLoad.failed') : $t('ModuleLoad.slow') }}</p>
            <button type="button" class="mls-retry" @click="$emit('retry')">
              <i class="el-icon-refresh-right" />
              {{ $t('ModuleLoad.retry') }}
            </button>
          </template>
          <p v-else class="mls-text">
            <i class="el-icon-loading" />
            {{ $t('ModuleLoad.loading') }}
          </p>
        </div>
      </div>
    </template>
    <slot v-else />
  </div>
</template>

<script>
export default {
  name: "ModuleLoadState",
  props: {
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    // 超过这个时间还在加载，显示「加载中…」
    slowAfter: { type: Number, default: 3000 },
    // 超过这个时间还在加载，显示「加载时间较长」和重试（请求本身不取消，回来了照常显示）
    timeout: { type: Number, default: 20000 },
    // 没有骨架插槽或骨架很矮时，保证提示有地方放
    minHeight: { type: String, default: "" },
    // 已经在加载时又发了一次请求（重试、中途改了筛选），把这个值加 1，计时会从头开始
    attempt: { type: [Number, String], default: 0 },
  },
  data() {
    return { phase: "loading" };
  },
  watch: {
    loading: {
      immediate: true,
      handler() {
        this.restartTimers();
      },
    },
    attempt() {
      this.restartTimers();
    },
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    restartTimers() {
      this.clearTimers();
      this.phase = "loading";
      if (!this.loading) return;
      this._slowTimer = setTimeout(() => { this.phase = "slow"; }, this.slowAfter);
      this._timeoutTimer = setTimeout(() => { this.phase = "timeout"; }, this.timeout);
    },
    clearTimers() {
      clearTimeout(this._slowTimer);
      clearTimeout(this._timeoutTimer);
    },
  },
};
</script>

<style scoped>
.mls-body {
  position: relative;
}
.mls-skeleton.is-dimmed {
  opacity: 0.55;
}
.mls-default-skeleton {
  margin-top: 0;
}
.mls-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  pointer-events: none;
}
.mls-text {
  margin: 0;
  padding: 6px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--color-text-secondary, #6B7280);
  font-size: 13px;
  line-height: 1.4;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.mls-retry {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--color-border, #E5E7EB);
  border-radius: 8px;
  background: #fff;
  color: var(--color-accent-primary, #1A365D);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.mls-retry:hover {
  background: #F9FAFB;
}
</style>
